import React, { useEffect, useState } from "react";
import { useStore } from "../../store/store.context";
import DonutChart from "../donut-chart/donut-chart";
import { fetchTodayEmailCount } from "../../services/case-summary-service";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator";

import "./case-summary.scss";

const StatusCard = ({
  status,
  count,
  helperText,
  onClick,
  isStart,
  isLast,
  isSelected,
}) => (
  <div className="stats" onClick={() => onClick(status)}>
    <div
      className={`statsSec ${isStart ? "_start" : ""} ${isLast ? "_last" : ""}`}
    >
      <p className="statsText" style={{ fontWeight: isSelected ? 700 : 400, fontSize: 'large' }}>{count}</p>
    </div>
    <span
      className={`statsHelperText ${isStart ? "_start" : ""} ${
        isLast ? "_last" : ""
      }`}
    >
      {helperText}
    </span>
  </div>
);

const CaseSummary = ({ apiState }) => {
  const { updateStore, store } = useStore();
  const [emailTotal, setEmailTotal] = useState(0);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);

  // If countries are selected in global store, compute counts from the full original data for those countries.
  // Important: `apiState.rows` may be already filtered by status (when a status card is clicked),
  // so use the original dataset from store.apiResponseData if available to compute counts for display.
  const selectedCountries = store.selectedCountries || [];
  const selectedStatus = store.dashboardStatus;

  const computeCounts = () => {
    // Prefer the original unfiltered data if available in the global store
    const fullData = (store.apiResponseData && store.apiResponseData.data) || apiState?.rows || [];

    // Count all rows whose casestatus ends with "-reopened" (covers all reopened sub-statuses)
    const computeReopened = (data) =>
      data.filter((r) => r.casestatus?.toLowerCase().endsWith("-reopened")).length;

    // If countries are selected, compute counts for those countries from the full dataset
    if (Array.isArray(selectedCountries) && selectedCountries.length > 0 && fullData.length > 0 && fullData.some(r => r.country)) {
      const filtered = fullData.filter((r) => selectedCountries.includes(r.country));
      const counts = filtered.reduce((acc, row) => {
        const s = row.casestatus?.toLowerCase();
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {});

      return {
        total: filtered.length,
        open: counts.open || 0,
        compliant: counts.compliant || 0,
        nonCompliant: counts["non-compliant"] || 0,
        approved: counts.approved || 0,
        rejected: counts.rejected || 0,
        onHold: counts["on-hold"] || 0,
        exception: counts.exception || 0,
        nonAfr: counts["non-afr"] || 0,
        reopened: computeReopened(filtered),
      };
    }

    // Otherwise return the already-calculated caseSummaryData (these are totals for all data)
    const {
      open,
      compliant,
      nonCompliant,
      approved,
      rejected,
      total,
      exception,
      onHold,
      nonAfr,
    } = apiState?.caseSummaryData || {};

    return { open, compliant, nonCompliant, approved, rejected, total, exception, onHold, nonAfr, reopened: computeReopened(fullData) };
  };

  const {
    open,
    compliant,
    nonCompliant,
    approved,
    rejected,
    total,
    exception,
    onHold,
    nonAfr,
    reopened,
  } = computeCounts();

  // Create donut data that mirrors the counts computed for display so the PieChart updates
  const donutDataForRender = [
    { name: "Open", value: open || 0 },
    { name: "Compliant", value: compliant || 0 },
    { name: "Non Compliant", value: nonCompliant || 0 },
    { name: "Approved", value: approved || 0 },
    { name: "Rejected", value: rejected || 0 },
    { name: "On-Hold", value: onHold || 0 },
    { name: "Exception", value: exception || 0 },
    { name: "Non AFR", value: nonAfr || 0 },
    { name: "Reopened", value: reopened || 0 },
  ];

  const handleStatusClick = (entry) => {
    // Toggle functionality: if the clicked status is already selected, deselect it
    const newStatus = selectedStatus === entry ? "" : entry;
    
    updateStore({
      dashboardStatus: newStatus,
      // make sure the main view shows dashboard/table and not prompt or a case detail
      prompt: false,
      caseId: "",
      initialCaseSelect: false,
    });
  };

  useEffect(() => {
    updateStore({ status: { mail: false } });

    // Skip fetch when navigating into a single-case detail view — CaseSummary will unmount shortly
    // and the result would be discarded.
    if (store.caseId) return;

    // Fetch today's email count filtered by selected status and countries
    const loadEmailCount = async () => {
      setIsLoadingEmail(true);
      try {
        // Pass the selected status to filter the email count
        const casestatus = selectedStatus ? selectedStatus.toUpperCase() : null;
        
        // If countries are selected, fetch count for each country and sum them
        if (selectedCountries.length > 0) {
          const counts = await Promise.all(
            selectedCountries.map(country => fetchTodayEmailCount(casestatus, country))
          );
          const totalCount = counts.reduce((sum, count) => sum + count, 0);
          setEmailTotal(totalCount);
        } else {
          // No country filter, fetch total count
          const count = await fetchTodayEmailCount(casestatus);
          setEmailTotal(count);
        }
      } finally {
        setIsLoadingEmail(false);
      }
    };
    
    loadEmailCount();
  }, [selectedStatus, selectedCountries, store.caseId]); // Re-fetch when selected status or countries change

  const statuses = [
    { status: "open", count: open, helperText: "Open" },
    { status: "compliant", count: compliant, helperText: "Compliant" },
    { status: "non-compliant", count: nonCompliant, helperText: "Non Compliant" },
    { status: "approved", count: approved, helperText: "Approved" },
    { status: "rejected", count: rejected, helperText: "Rejected" },
    { status: "on-hold", count: onHold, helperText: "On Hold" },
    { status: "exception", count: exception, helperText: "Exception" },
    { status: "non-afr", count: nonAfr || 0, helperText: "Non AFR" },
    { status: "reopened", count: reopened || 0, helperText: "Reopened" },
  ];
  // Determine which label to show based on the selected prompt
  const promptQuery = store.promptQuery?.toLowerCase() || "";
  // Use a looser match for the backlog prompt to handle date variations (app left open across midnight, etc.)
  // The backlog query always starts with "show me the case summary from"
  const isBacklogPrompt = promptQuery.startsWith("show me the case summary from");
  const isCaseSummaryPrompt = promptQuery === "show me the case summary for last 6 days";

  // Dynamic heading for summary
  let summaryHeading = null;
  if (isBacklogPrompt) summaryHeading = "Backlog Case Summary";
  else if (isCaseSummaryPrompt) summaryHeading = "Case Summary";
  else summaryHeading = "";

  return (
    <div className="mds-grid" style={{ maxWidth: "100%", overflow: "hidden", display: 'flex' }}>
      <div className="header-section mds-grid-col-1 mds-grid-col-span-6" style={{width: '56%'}}>
        {summaryHeading && <span className="outer-heading">{summaryHeading}</span>}
        {/* <span className="sub-heading">12th January , 2025 | 6:12 PM</span> */}

        <div className="stats-Section">
          <span className="inner-heading">
            {isBacklogPrompt ? (
              <>Total No. of backlog cases <b>{total}</b></>
            ) : isCaseSummaryPrompt ? (
              <>Total No. of cases <b>{total}</b></>
            ) : null}
          </span>
          <span className="inner-heading" style={{display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '20px'}}>
            <span>Total No. Emails received today</span>
            {isLoadingEmail ? (
              <McLoadingIndicator appearance="primary" />
            ) : (
              <b>{emailTotal}</b>
            )}
          </span>
          <div style={{ paddingTop: 10 }}>
            <div className="mds-flex statsGrid">
              {statuses.map((statusItem, index) => (
                <StatusCard
                  key={statusItem.status}
                  status={statusItem.status}
                  count={statusItem.count}
                  helperText={statusItem.helperText}
                  onClick={handleStatusClick}
                  isStart={index === 0} // First item gets _start
                  isLast={index === statuses.length - 1} // Last item gets _last
                  isSelected={selectedStatus === statusItem.status}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {donutDataForRender && (
        <div className="mds-grid-col-7" style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "44%" }}>
          <DonutChart donutData={donutDataForRender} />
        </div>
      )}
    </div>
  );
};

export default CaseSummary;
